import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { siteInfo } from "@/mocks/site";

const navItems = [
  { label: "首页", to: "/", key: "home" },
  { label: "年度盘点", to: "/archive", key: "archive" },
  { label: "历史复盘", to: "/archive?view=review", key: "review" },
  { label: "关于本站", to: "/about", key: "about" },
];

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const isReview = location.search.includes("view=review");

  const isActive = (key: string) => {
    if (key === "home") return location.pathname === "/";
    if (key === "archive") return location.pathname === "/archive" && !isReview;
    if (key === "review") return location.pathname === "/archive" && isReview;
    if (key === "about") return location.pathname === "/about";
    return false;
  };

  const runSearch = (event: FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/archive?q=${encodeURIComponent(q)}` : "/archive");
    setKeyword("");
    setMenuOpen(false);
  };

  return (
    <header className="relative z-50 w-full">
      <div className="hidden bg-secondary-950 text-background-100 md:block">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-2 text-xs">
          <p className="flex items-center gap-2">
            <i className="ri-history-line text-accent-500" />
            {siteInfo.slogan}
          </p>
          <p className="flex items-center gap-2 text-background-200">
            <i className="ri-mail-line" />
            {siteInfo.email}
          </p>
        </div>
      </div>

      <div
        className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
          scrolled
            ? "border-background-200 bg-background-50/95 backdrop-blur-md"
            : "border-transparent bg-background-50"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between gap-4 px-4 md:h-20 md:px-6">
          <Link to="/" className="flex shrink-0 items-center gap-2.5" aria-label={siteInfo.name}>
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-flashlight-fill text-lg" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-bold tracking-wide text-foreground-950 md:text-xl">
                {siteInfo.name}
              </span>
              <span className="mt-1 hidden text-[11px] text-foreground-500 sm:block">
                {siteInfo.tagline}
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
            {navItems.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive(item.key)
                    ? "bg-primary-50 text-primary-700"
                    : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form onSubmit={runSearch} className="hidden items-center md:flex" role="search">
              <div className="relative">
                <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  type="search"
                  name="keyword"
                  placeholder="搜索事件、关键词"
                  aria-label="搜索事件"
                  className="h-10 w-40 rounded-md border border-background-300 bg-background-50 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-all placeholder:text-foreground-400 focus:w-56 focus:border-primary-400 focus:ring-2 focus:ring-primary-200 xl:w-48"
                />
              </div>
              <button
                type="submit"
                className="ml-2 hidden cursor-pointer whitespace-nowrap rounded-md bg-foreground-950 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-foreground-800 xl:inline-flex"
              >
                搜一搜
              </button>
            </form>

            <button
              type="button"
              aria-label="打开菜单"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 transition-colors hover:bg-background-100 lg:hidden"
            >
              <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="fixed inset-x-0 bottom-0 top-16 z-40 overflow-y-auto bg-background-50 px-4 pb-10 pt-4 md:top-20 lg:hidden">
          <form onSubmit={runSearch} className="mb-4 flex items-center gap-2" role="search">
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="search"
                name="mobile-keyword"
                placeholder="搜索事件、关键词"
                aria-label="搜索事件"
                className="h-11 w-full rounded-md border border-background-300 bg-background-50 pl-9 pr-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="h-11 shrink-0 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-medium text-background-50"
            >
              搜索
            </button>
          </form>

          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            {navItems.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                className={`flex items-center justify-between rounded-lg px-4 py-3.5 text-base font-medium transition-colors ${
                  isActive(item.key)
                    ? "bg-primary-50 text-primary-700"
                    : "text-foreground-800 hover:bg-background-100"
                }`}
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-foreground-400" />
              </Link>
            ))}
          </nav>

          <div className="mt-6 rounded-lg bg-background-100 p-4">
            <p className="text-xs leading-relaxed text-foreground-600">{siteInfo.intro}</p>
            <p className="mt-3 flex items-center gap-2 text-xs text-foreground-500">
              <i className="ri-mail-line" />
              {siteInfo.email}
            </p>
          </div>
        </div>
      )}
    </header>
  );
}